import { Box, Divider, Switch, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { grey } from '../../../../colors'
import GenericButton from '../../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'
import { branchModal, getEditableQuestion, getSelectedQuestion, getSurveySections } from '../../../../store/slices/SurveyTemplateSlice/selectors'
import { type ISurveyItemBranchingFilters, type Question, SurveyItemQuestionType } from '../../types'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { ReactComponent as BranchingIcon } from '../../../../assets/branching.svg'
import { ReactComponent as DeleteIcon } from '../../../../assets/trash.svg'
import BranchModal from '../BranchModal'
import { useAppDispatch } from '../../../../store'
import { type ISurveySection, removeSection, setBranchModal, setEditableQuestion } from '../../../../store/slices/SurveyTemplateSlice'
import { checkQuestionValidation, getUniqueChildBranchesDetails, scrollToElement, updateElementBorderColor } from '../../helpers'
import GenericModal from '../../../../components/GenericModal'
import Dropdown from '../../../../components/Dropdown'
import { notification } from '../../../../components/Notifications'
import CustomTooltip from '../../../../components/CustomTooltip'
import { BRANCHING_TOOLTIP_TITLE, DUPLICATE_TOOLTIP_TITLE } from '../../constant'

interface QuestionFooterProps {
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  handleDuplicate?: () => void
  questionData: Question
  questionIndex: number
  handleClickAway?: () => void
  section: ISurveySection
  isAdminAddedQuestion?: boolean
  isCountComplete?: boolean
}
const QuestionFooter: React.FC<QuestionFooterProps> = ({ handleChange, handleDuplicate, questionData, questionIndex, handleClickAway, section, isAdminAddedQuestion, isCountComplete }) => {
  const [deleteBranchModal, setDeleteBranchModal] = useState<boolean>(false)
  const [linkedBranchModal, setLinkedBranchModal] = useState<boolean>(false)
  const [selectedBranchForDeletion, setSelectedBranchForDeletion] = useState<string | null>(null)
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const selectedQuestion = getSelectedQuestion()
  const editableQuestion = getEditableQuestion()
  const allSections = getSurveySections()
  const isQuestionValid = checkQuestionValidation(questionData)
  const isBranchModalOpen = branchModal()

  const hideDuplicateButton = selectedQuestion?.questionType === SurveyItemQuestionType.FREE_RESPONSE || editableQuestion?.question?.itemType === SurveyItemQuestionType.FREE_RESPONSE || isCountComplete

  const handleBranchModal = (): void => {
    dispatch(setBranchModal(true))
  }

  const scrollToBranchSection = (surveySectionId: string): void => {
    scrollToElement(surveySectionId, 'blue')

    // Remvoe color
    setTimeout(() => {
      updateElementBorderColor(surveySectionId, '')
    }, 2000)
  }

  const closeDeleteBranchModal = (): void => {
    setDeleteBranchModal(false)
  }

  const openDeleteBranchModal = (surveySectionId: string): void => {
    setDeleteBranchModal(true)
    setSelectedBranchForDeletion(surveySectionId)
  }

  const deleteBranchSection = async (): Promise<void> => {
    const toBeDeletedBranch = allSections.find((s) => s.surveySectionId === selectedBranchForDeletion)

    if (toBeDeletedBranch) {
      // Before deleting, we need to remove the focus. During editing, the questionData is in local state, and on clickaway, the old data is saved in the state
      dispatch(setEditableQuestion(null))
      dispatch(removeSection({ section: toBeDeletedBranch }))
      closeDeleteBranchModal()
      notification('Branch Successfully Deleted', { variant: 'success' })
    }
    setSelectedBranchForDeletion(null)
  }

  const isQuestionHaveBranchFilters = questionData?.branchingFilters && questionData?.branchingFilters.length > 0

  const branchFeatureButton = (): JSX.Element => {
    const branchingTooltipTitle = isQuestionValid ? '' : BRANCHING_TOOLTIP_TITLE
    const menuItems = [
      { text: 'Create New Branch', action: handleBranchModal },
      { text: 'Linked Branches', action: () => { setLinkedBranchModal(true) }, disabled: !isQuestionValid, tooltipTitle: branchingTooltipTitle }
    ]

    return isQuestionHaveBranchFilters
      ? <Dropdown menuItems={menuItems} label={<BranchingIcon />} extraButtonProps={{ buttonType: ButtonType.Secondary }} />
      : <CustomTooltip title={branchingTooltipTitle}>
        <GenericButton
          disabled={!isQuestionValid}
          buttonSize={ButtonSize.Medium}
          icon={<BranchingIcon opacity={!isQuestionValid ? 0.3 : 1} />}
          fontWeight='bold'
          buttonType={ButtonType.Secondary}
          onClick={handleBranchModal} />
      </CustomTooltip>
  }

  const duplicateButton = (): JSX.Element | null => {
    const duplicateButtonTooltipTitle = editableQuestion ? '' : DUPLICATE_TOOLTIP_TITLE

    if (hideDuplicateButton) return null
    return (
      <CustomTooltip title={duplicateButtonTooltipTitle}>
        <GenericButton
          buttonSize={ButtonSize.Medium}
          icon={<ContentCopyIcon />}
          fontWeight="bold"
          buttonType={ButtonType.Secondary}
          onClick={handleDuplicate}
          disabled={!editableQuestion}
        />
      </CustomTooltip>
    )
  }

  const childBranchDetails = getUniqueChildBranchesDetails(questionData.branchingFilters)
  return (
    <>
      <Divider sx={{ my: theme.spacing(6) }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: theme.spacing(2), mt: theme.spacing(6) }}>
        <Box display='flex' gap={2} justifyContent='center' alignItems='center'>
          <>
            {duplicateButton()}
            {!isAdminAddedQuestion && branchFeatureButton()}
          </>
        </Box>
        <Box display='flex' justifyContent='center' alignItems='center'>
          <Switch
            checked={questionData?.isRequired}
            onChange={handleChange}
          />
          <Typography sx={{ ml: 2, fontSize: '16px', fontWeight: 600, color: grey[250] }}>Required</Typography>
        </Box>
        {isBranchModalOpen && <BranchModal isOpen={isBranchModalOpen} section={section} questionIndex={questionIndex} questionData={questionData} handleClickAway={handleClickAway} />}

        {linkedBranchModal &&
          <GenericModal isOpen={linkedBranchModal} maxWidth='sm' title="Linked Branches" onClose={() => { setLinkedBranchModal(false) }} hideConfirmButton destructiveButtonType={ButtonType.Secondary}>
            {
              childBranchDetails?.map((branch) => {
                return (
                  <Box display='flex' flexDirection='column' gap={3} mt={5} key={branch.surveySectionId}>
                    <Box display='flex' justifyContent='space-between' gap={2} alignItems={'center'}>
                      <Typography>{branch?.surveySectionName} </Typography>
                      <Box display='flex' alignItems='center' gap={3}>
                        <GenericButton style={{ padding: 1 }} icon={<BranchingIcon />} onClick={() => { scrollToBranchSection(branch.surveySectionId) }} />
                        <GenericButton style={{ padding: 1 }} icon={<DeleteIcon />} onClick={() => { openDeleteBranchModal(branch.surveySectionId) }} />
                      </Box>
                    </Box>
                  </Box>
                )
              })
            }
            <Divider sx={{ mt: 4 }} />
          </GenericModal>
        }

        {deleteBranchModal &&
          <GenericModal isOpen={deleteBranchModal} maxWidth='sm' title="Delete Branch" onClose={closeDeleteBranchModal} confirmActionText='Confirm' destructiveButtonType={ButtonType.Secondary} onConfirm={deleteBranchSection}>
            <Typography mt={4}>Are you sure you want to delete the branch section linked to this question?</Typography>
            <Divider sx={{ mt: 10 }} />
          </GenericModal>
        }
      </Box>
    </>
  )
}

export default QuestionFooter
